import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { type VariantProps } from 'class-variance-authority';
import isEmpty from 'just-is-empty';
import { useState, type JSX } from 'react';

import { Button, buttonVariants } from '../../primitives/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../primitives/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { ScrollArea } from '../../primitives/scroll-area';
import { OptionInfoField, OptionInfoPopover } from '../option-info-popover';

import { cn } from '../../utils/cn';

type TComboboxModelActionButton = {
  icon?: JSX.Element;
  text?: string;
  onClick: VoidFunction;
};

export const ComboboxSeparator = 'ComboboxSeparator' as const;

type TComboboxModelSeparator = {
  item: typeof ComboboxSeparator;
};

type TComboboxModelOption = {
  button?: TComboboxModelActionButton;
  infoFields?: OptionInfoField[];
  key?: string; // Also use `key` to filter if is defined
  value: string;
  item: string | JSX.Element;
};

export type TComboboxModelItem = TComboboxModelOption | TComboboxModelSeparator;
// Yup another controversial name. Probably the exporting system needs to be improved
// to only export/import the component you want from the lib (not all the set), with that
// in mind this type could be just Model and renamed on import side with `as` if needed.
export type ComboboxModelModel = TComboboxModelItem[];

type TTriggerText = string | { text: string; className?: string };

interface ComboboxModelProps extends React.ComponentPropsWithoutRef<typeof CommandItem> {
  button?: {
    onClick: (searchValue: string) => void;
    text: string;
  };
  classNameCommandList?: string;
  emptyOptionsText?: TTriggerText;
  fuzzy?: boolean;
  model: ComboboxModelModel;
  placeholder?: TTriggerText;
  searchPlaceholder?: string;
  size?: VariantProps<typeof buttonVariants>['size'];
  variant?: VariantProps<typeof buttonVariants>['variant'];
}

function isComboboxSeparator(modelItem: TComboboxModelItem): modelItem is TComboboxModelSeparator {
  return modelItem.item === ComboboxSeparator;
}

function isComboboxOption(modelItem: TComboboxModelItem): modelItem is TComboboxModelOption {
  return !isComboboxSeparator(modelItem);
}

export function ComboboxModel({
  button,
  className,
  classNameCommandList,
  disabled,
  emptyOptionsText = 'No hay opciones',
  fuzzy = false,
  id,
  model,
  placeholder = 'Seleccionar',
  searchPlaceholder = 'Buscar',
  size,
  value,
  variant = 'outline',
  onSelect,
}: ComboboxModelProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const selectedValue = typeof value === 'string' ? value : undefined;
  const optionItems = model.filter(isComboboxOption);

  const selectedOption = selectedValue
    ? optionItems.find((modelItem) => modelItem.value === selectedValue)
    : undefined;

  const selectedOptionInfoFields =
    selectedOption && selectedOption.value && selectedOption.item
      ? selectedOption.infoFields
      : undefined;

  const filter = fuzzy
    ? undefined // fuzzy
    : (itemValue: string, search: string, keywords: string[] = []) => {
        // contiguous matches against the main value and provided keywords.
        const term = search.trim().toLowerCase();
        if (!term) return 1;

        const searchableText = [itemValue, ...keywords].join(' ').toLowerCase();
        return searchableText.includes(term) ? 1 : 0;
      };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearchValue('');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className={cn('group flex w-full justify-between gap-1', className)}
          disabled={disabled || (optionItems.length === 0 && !button)}
          id={id}
          role="combobox"
          size={size}
          variant={variant}
        >
          <span className="inline-flex min-w-0 flex-1 items-center gap-1">
            <PopoverTriggerButton
              emptyOptionsText={emptyOptionsText}
              model={model}
              value={value}
              placeholder={placeholder}
            />
            <OptionInfoPopover fields={selectedOptionInfoFields} />
          </span>
          <ChevronDownIcon className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) p-0"
      >
        <Command filter={filter} defaultValue={value}>
          <CommandInput
            button={
              button
                ? {
                    text: button.text,
                    onClick: () => {
                      button.onClick(searchValue);
                      setOpen(false);
                      setSearchValue('');
                    },
                  }
                : undefined
            }
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList className={cn('max-h-[min(10rem,55vh)]', classNameCommandList)}>
            <ScrollArea>
              <CommandEmpty>Sin resultados 🫥</CommandEmpty>
              <CommandGroup>
                {model.map((modelItem, index) =>
                  isComboboxSeparator(modelItem) ? (
                    <CommandSeparator key={`combobox-separator-${index}`} className="my-1" />
                  ) : (
                    <CommandItem
                      id={id ? `${id}-${modelItem.value}` : undefined}
                      className="justify-between"
                      keywords={[modelItem?.key ?? '', modelItem.value]}
                      key={modelItem.value}
                      value={modelItem.value}
                      onSelect={() => {
                        onSelect && onSelect(modelItem.value);
                        setOpen(false);
                        setSearchValue('');
                      }}
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <span
                          className={cn('line-clamp-2 w-full min-w-0 break-all', {
                            'text-xs': size === 'xs',
                            'text-sm': size === 'sm',
                          })}
                        >
                          {modelItem.item}
                        </span>

                        <OptionInfoPopover fields={modelItem.infoFields} />

                        {value === modelItem.value && typeof modelItem.item === 'string' ? (
                          <CheckIcon className="ml-1 size-4 shrink-0" />
                        ) : null}

                        {modelItem.button ? (
                          <Button
                            className="ml-1 h-auto min-h-0 shrink-0 px-1"
                            size="xs"
                            variant="link"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              modelItem.button?.onClick();
                            }}
                          >
                            {modelItem.button.icon}
                            {modelItem.button.text}
                          </Button>
                        ) : null}
                      </span>
                    </CommandItem>
                  ),
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface PopoverTriggerButtonProps {
  emptyOptionsText: TTriggerText;
  model: ComboboxModelModel;
  placeholder: TTriggerText;
  value?: string;
}

function resolveTriggerText(content: TTriggerText): { text: string; className?: string } {
  return typeof content === 'string' ? { text: content } : content;
}

function PopoverTriggerButton({
  emptyOptionsText,
  model,
  placeholder,
  value,
}: PopoverTriggerButtonProps) {
  const resolvedEmptyOptionsText = resolveTriggerText(emptyOptionsText);
  const resolvedPlaceholder = resolveTriggerText(placeholder);
  const optionItems = model.filter(isComboboxOption);

  if (optionItems.length === 0) {
    return (
      <span className={cn('truncate', resolvedEmptyOptionsText?.className)}>
        {resolvedEmptyOptionsText.text}
      </span>
    );
  }

  if (value && !isEmpty(value)) {
    return (
      <span className="truncate">
        {optionItems.find((modelItem) => modelItem.value === value)?.item ?? value}
      </span>
    );
  }

  return (
    <span className={cn('truncate', resolvedPlaceholder?.className)}>
      {resolvedPlaceholder.text}
    </span>
  );
}
